import LocalizationDisclosure from '../components/localization/LocalizationDisclosure';
import MobileSearch from '../components/search/MobileSearch';
import Scroll from '../helpers/scroll';
import Search from '../components/search/Search';
import Splide from '@splidejs/splide';
import { unitlessBreakpoints } from '../helpers';
import { disablePageScroll, enablePageScroll } from '@fluejs/noscroll';

const selectors = {
  headerNavigation: '[data-header-navigation]',
  navHamburger: '[data-nav-hamburger]',
  navClose: '[data-nav-close]',
  megaDetails: '[data-mega-details]',
  megaBackButton: '[data-submenu-back]',
  megamenuCarousel: '[data-megamenu-carousel]',
  progressBar: '[data-progress-bar]',
};

const MEGA_HOVER_OPEN_DELAY_MS = 120;
const MEGA_HOVER_CLOSE_DELAY_MS = 400;

export default class MainHeader {
  constructor(section) {
    this.section = section;

    this.listener = new Scroll(this.onScroll, this.section.el);

    new Search(section);
    new MobileSearch(section);

    this.disableDesktopScroll();
    this.initDesktopMegaHover();
    document.addEventListener('keydown', this.onMegaEscape);
    this.initMobileNavigation();
    this.initMegamenuCarousel();
    LocalizationDisclosure.init(document.body);
  }

  onSectionUnload() {
    document.removeEventListener('keydown', this.onMegaEscape);
  }

  // Document-level so a hover-opened menu closes too, not only one holding focus.
  onMegaEscape = e => {
    if (e.key !== 'Escape') {
      return;
    }
    this.section.el
      .querySelectorAll(`${selectors.megaDetails}[open]`)
      .forEach(detail => {
        const hadFocus = detail.contains(document.activeElement);
        detail.open = false;
        if (hadFocus) {
          detail.querySelector('summary')?.focus();
        }
      });
  };

  onScroll = direction => {
    if (direction === 'up') {
      this.showMenu();
    } else if (direction === 'down') {
      this.hideMenu();
    }
  };

  showMenu = () => {
    this.section.el.classList.remove('-translate-y-full');
  };

  hideMenu = () => {
    this.section.el.classList.add('-translate-y-full');
  };

  disableDesktopScroll = () => {
    const megaDetails = this.section.el.querySelectorAll(selectors.megaDetails);
    megaDetails.forEach(detail => {
      detail.addEventListener('toggle', ({ newState }) => {
        if (window.innerWidth >= unitlessBreakpoints.lg) {
          if (newState === 'open') {
            disablePageScroll();
          } else {
            enablePageScroll();
          }
        }
      });
    });
  };

  initDesktopMegaHover = () => {
    const megaDetails = this.section.el.querySelectorAll(selectors.megaDetails);

    const useMegaHover = () =>
      window.innerWidth >= unitlessBreakpoints.lg &&
      window.matchMedia('(hover: hover)').matches;

    megaDetails.forEach(detail => {
      const summary = detail.querySelector('summary');
      if (!summary) {
        return;
      }

      let openTimeoutId = null;
      let closeTimeoutId = null;

      detail.addEventListener('mouseenter', () => {
        if (closeTimeoutId != null) {
          clearTimeout(closeTimeoutId);
          closeTimeoutId = null;
        }
        if (!useMegaHover()) {
          return;
        }
        if (openTimeoutId != null) {
          clearTimeout(openTimeoutId);
        }
        openTimeoutId = setTimeout(() => {
          openTimeoutId = null;
          detail.open = true;
        }, MEGA_HOVER_OPEN_DELAY_MS);
      });

      detail.addEventListener('mouseleave', () => {
        if (openTimeoutId != null) {
          clearTimeout(openTimeoutId);
          openTimeoutId = null;
        }
        if (!useMegaHover()) {
          return;
        }
        closeTimeoutId = setTimeout(() => {
          closeTimeoutId = null;
          detail.open = false;
        }, MEGA_HOVER_CLOSE_DELAY_MS);
      });

      summary.addEventListener('click', e => {
        if (useMegaHover() && e.detail > 0) {
          e.preventDefault();
        }
      });
    });
  };

  initMobileNavigation = () => {
    const megaBackButtons = this.section.el.querySelectorAll(
      selectors.megaBackButton,
    );

    this.openMobileNav();
    this.closeMobileNav();

    // Set subnav heights
    megaBackButtons.forEach(button => {
      button.addEventListener('click', () => {
        const parentDetail = button.closest('details');
        parentDetail.removeAttribute('open');
      });
    });
  };

  openMobileNav = () => {
    const headerNavigation = this.section.el.querySelector(
      selectors.headerNavigation,
    );
    const navHamburger = this.section.el.querySelector(selectors.navHamburger);
    const navClose = this.section.el.querySelector(selectors.navClose);
    navHamburger.addEventListener('click', () => {
      const totalHeaderHeight = this.getHeaderHeight();
      headerNavigation.style.top = `${totalHeaderHeight - 1}px`;
      navHamburger.classList.add('hidden');
      navClose.classList.remove('hidden');
      headerNavigation.classList.remove('hidden');
      headerNavigation.classList.add('flex');
      disablePageScroll();
    });
  };

  closeMobileNav = () => {
    const headerNavigation = this.section.el.querySelector(
      selectors.headerNavigation,
    );
    const navHamburger = this.section.el.querySelector(selectors.navHamburger);
    const navClose = this.section.el.querySelector(selectors.navClose);
    navClose.addEventListener('click', () => {
      navHamburger.classList.remove('hidden');
      navClose.classList.add('hidden');
      headerNavigation.classList.add('hidden');
      headerNavigation.classList.remove('flex');
      enablePageScroll();
    });
  };

  getHeaderHeight = () => {
    return this.section.el.offsetHeight;
  };

  initMegamenuCarousel = () => {
    const megamenuCarouselList = this.section.el.querySelectorAll(
      selectors.megamenuCarousel,
    );

    megamenuCarouselList.forEach(carousel => {
      const megamenuCarousel = new Splide(carousel, {
        perPage: 1,
        pagination: false,
        arrows: true,
        gap: 0,
      });

      const progressBar = megamenuCarousel.root.querySelector(
        selectors.progressBar,
      );
      megamenuCarousel.on('mounted move', () => {
        const end = megamenuCarousel.Components.Controller.getEnd() + 1;
        const rate = Math.min((megamenuCarousel.index + 1) / end, 1);
        progressBar.style.transform = `scaleX(${rate})`;
      });
      megamenuCarousel.mount();
    });
  };
}
